import moment from "moment";
import { useParams, useNavigate } from "react-router-dom";
import { HashLoader } from "react-spinners";
import {
  AirplaneInFlight,
  ArrowClockwise,
  ArrowLeft,
  SealCheck,
  Ticket,
  UsersThree,
  Wallet,
  Warning,
} from "@phosphor-icons/react";
import { formatAsMoney } from "../../../helpers/numbers";
import useGetInternationalBookingDetail from "../../../hooks/useGetInternationalBookingDetail";
import useGetInternationalOrderTicket from "../../../hooks/useGetInternationalOrderTicket";
import { IntlBookingDetailSlice } from "../../../@types/intlFlightAggregator";
import AviationAmbientBackground from "../../../components/AviationAmbientBackground";

const cardClass =
  "bg-white rounded-2xl shadow-[0_4px_20px_-4px_rgba(15,23,42,0.10)] border border-gray-100 p-5 flex flex-col gap-4";

const SliceCard = ({ slice, label }: { slice: IntlBookingDetailSlice; label?: string }) => (
  <div className="flex flex-col gap-3">
    {label && <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{label}</span>}
    {slice.segments.map((segment, index) => (
      <div key={index} className="flex flex-col gap-1">
        {index > 0 && <div className="h-px bg-gray-100 my-1" />}
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-start min-w-[70px]">
            <span className="text-xl font-bold font-aeonic text-deepBlue">{segment.origin ?? "—"}</span>
            <span className="text-xs font-bold text-gray-400">
              {segment.departure_at ? moment(segment.departure_at).format("HH:mm, D MMM") : "—"}
            </span>
            {segment.origin_terminal && (
              <span className="text-[10px] text-gray-400">Terminal {segment.origin_terminal}</span>
            )}
          </div>
          <div className="flex-1 flex flex-col items-center gap-1 min-w-0 px-1">
            <span className="text-[10px] text-gray-400 font-medium truncate">
              {segment.airline}
              {segment.flight_number ? ` · ${segment.flight_number}` : ""}
            </span>
            <div className="flex items-center w-full gap-1">
              <div className="h-[1px] flex-1 bg-gray-200" />
              <AirplaneInFlight className="size-3.5 shrink-0 text-visaro-orange" weight="fill" />
              <div className="h-[1px] flex-1 bg-gray-200" />
            </div>
          </div>
          <div className="flex flex-col items-end min-w-[70px]">
            <span className="text-xl font-bold font-aeonic text-deepBlue">{segment.destination ?? "—"}</span>
            <span className="text-xs font-bold text-gray-400">
              {segment.arrival_at ? moment(segment.arrival_at).format("HH:mm, D MMM") : "—"}
            </span>
            {segment.destination_terminal && (
              <span className="text-[10px] text-gray-400">Terminal {segment.destination_terminal}</span>
            )}
          </div>
        </div>
        {index < slice.segments.length - 1 && (
          <div className="self-center text-[10px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full mt-1">
            Layover
          </div>
        )}
      </div>
    ))}
  </div>
);

const FlightDetails = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();

  const { booking, fetchingBooking, bookingError } = useGetInternationalBookingDetail(bookingId);
  const { ticket, fetchTicket, isFetchingTicket, hasFetchedTicket } = useGetInternationalOrderTicket(bookingId, {
    enabled: !!bookingId,
  });

  const documents = ticket?.documents ?? booking?.documents ?? [];

  if (fetchingBooking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <HashLoader color="#EA5C29" />
      </div>
    );
  }

  if (bookingError || !booking) {
    return (
      <div className="min-h-screen bg-gray-50 p-10 flex flex-col items-center justify-center gap-4 text-center">
        <Warning size={32} className="text-red-500" />
        <p className="text-gray-500">Unable to load this booking.</p>
        <button
          type="button"
          onClick={() => navigate("/aviation/your-flights", { state: { tab: "international" } })}
          className="bg-visaro-orange text-white px-6 py-3 rounded-xl font-semibold"
        >
          Back to Your Flights
        </button>
      </div>
    );
  }

  const firstSlice = booking.slices[0];

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      <AviationAmbientBackground />

      <div className="relative p-5 lg:p-10 py-10 flex flex-col gap-6 max-w-3xl mx-auto">
        <button
          type="button"
          onClick={() => navigate("/aviation/your-flights", { state: { tab: "international" } })}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-visaro-orange transition-colors duration-200 self-start"
        >
          <ArrowLeft size={16} weight="bold" />
          Back to Your Flights
        </button>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
              {firstSlice?.origin} → {firstSlice?.destination}
            </span>
            <h1 className="text-2xl font-bold text-deepBlue">Flight Details</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-visaro-visaGreen bg-visaro-visaMintGreen px-2.5 py-1 rounded-full">
              <SealCheck size={12} weight="fill" />
              {booking.status}
            </span>
            {booking.booking_reference && (
              <span className="text-lg font-bold font-aeonic text-deepBlue tracking-widest">
                {booking.booking_reference}
              </span>
            )}
          </div>
        </div>

        <div className={cardClass}>
          <div className="flex items-center gap-2.5">
            <span className="flex items-center justify-center size-9 rounded-full bg-deepBlue/5 text-deepBlue shrink-0">
              <AirplaneInFlight size={18} weight="duotone" />
            </span>
            <h2 className="font-bold text-deepBlue">Itinerary</h2>
          </div>
          <div className="flex flex-col gap-4">
            {booking.slices.map((slice, index) => (
              <SliceCard
                key={index}
                slice={slice}
                label={booking.slices.length > 1 ? (index === 0 ? "Outbound" : "Return") : undefined}
              />
            ))}
          </div>
        </div>

        <div className={cardClass}>
          <div className="flex items-center gap-2.5">
            <span className="flex items-center justify-center size-9 rounded-full bg-deepBlue/5 text-deepBlue shrink-0">
              <UsersThree size={18} weight="duotone" />
            </span>
            <h2 className="font-bold text-deepBlue">Passengers</h2>
          </div>
          <div className="flex flex-col gap-2">
            {booking.passengers.map((passenger, index) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <span className="text-deepBlue font-medium">
                  {passenger.given_name} {passenger.family_name}
                </span>
                <span className="text-gray-400 capitalize">{passenger.type}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={cardClass}>
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-deepBlue">Price &amp; Payment</h2>
          </div>
          <div className="flex flex-col gap-2.5 text-sm">
            <div className="flex items-center justify-between text-gray-500">
              <span>Total price</span>
              <span className="font-aeonic font-bold text-deepBlue">
                {booking.currency} {booking.price !== null ? formatAsMoney(booking.price) : "—"}
              </span>
            </div>
            {booking.price_ngn !== null && (
              <div className="flex items-center justify-between text-gray-400 text-xs">
                <span>Approx. in Naira</span>
                <span>≈ ₦{formatAsMoney(booking.price_ngn)}</span>
              </div>
            )}
            <div className="h-px bg-gray-100 my-1" />
            {booking.payment_method === "wallet" && (
              <div className="flex items-center justify-between text-gray-500">
                <span className="flex items-center gap-1.5">
                  <Wallet size={14} weight="fill" className="text-visaro-orange" />
                  Paid via Wallet
                </span>
                <span className="font-semibold text-deepBlue">
                  {booking.paid_amount_ngn !== null ? `₦${formatAsMoney(booking.paid_amount_ngn)}` : "—"}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className={cardClass}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Ticket size={16} weight="duotone" className="text-visaro-orange" />
              <h2 className="text-sm font-bold text-deepBlue">Your e-ticket</h2>
            </div>
            {hasFetchedTicket && (
              <button
                type="button"
                onClick={() => fetchTicket()}
                disabled={isFetchingTicket}
                className="inline-flex items-center gap-1 text-xs font-semibold text-visaro-orange hover:text-visaro-orange/80 disabled:opacity-50 transition-colors duration-200"
              >
                <ArrowClockwise size={12} weight="bold" className={isFetchingTicket ? "animate-spin" : ""} />
                Check again
              </button>
            )}
          </div>

          {isFetchingTicket && !hasFetchedTicket ? (
            <p className="text-xs text-gray-400">Checking with the airline…</p>
          ) : documents.length > 0 ? (
            <div className="flex flex-col gap-2">
              {documents.map((doc, index) => (
                <div key={doc.unique_identifier + index} className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">E-ticket number</span>
                  <span className="font-bold font-aeonic text-deepBlue tracking-wide">{doc.unique_identifier}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400">
              Your e-ticket is still being issued by the airline — this usually takes a few minutes. Check back
              here, or quote your booking reference directly with the airline.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlightDetails;
